import React from 'react';
import styled, { css } from '@emotion/native';
import Button from '@/shared/components/Button';
import useBreakpoint from '@/shared/hooks/useBreakpoint';

interface PendingRequestActionsProps {
  onAccept: () => void;
  onDecline: () => void;
  onViewDetails: () => void;
  approving: boolean;
  declining: boolean;
}

/**
 * Renders accept/decline buttons for pending in-person requests.
 * - Desktop: Shows View Details, Decline, Accept in a row at the bottom
 * - Mobile: Shows Decline, Accept at the top (rendered separately via MobileTopButtons)
 *           and View Details at the bottom
 */
const PendingRequestActions = ({
  onAccept,
  onDecline,
  onViewDetails,
  approving,
  declining,
}: PendingRequestActionsProps) => {
  const { isDesktop } = useBreakpoint();
  const isDisabled = approving || declining;

  if (isDesktop) {
    return (
      <DesktopButtonsRow>
        <Button
          customStyle={[desktopButtonStyles, { marginRight: 9 }]}
          customLabelStyle={buttonFontStyle}
          text="View Details"
          onPress={onViewDetails}
          variant="tertiary"
        />
        <Button
          customStyle={[desktopButtonStyles, { marginRight: 9 }]}
          customLabelStyle={buttonFontStyle}
          text="Decline"
          onPress={onDecline}
          variant="tertiary"
          loading={declining}
          disabled={isDisabled}
        />
        <Button
          customStyle={desktopButtonStyles}
          customLabelStyle={buttonFontStyle}
          text="Accept"
          onPress={onAccept}
          variant="primary"
          loading={approving}
          disabled={isDisabled}
        />
      </DesktopButtonsRow>
    );
  }

  // Mobile: Just the View Details button (Accept/Decline are at top via MobileTopButtons)
  return (
    <MobileViewDetailsContainer>
      <Button
        customStyle={mobileViewDetailsButtonStyles}
        customLabelStyle={buttonFontStyle}
        text="View Details"
        onPress={onViewDetails}
        variant="tertiary"
      />
    </MobileViewDetailsContainer>
  );
};

/**
 * Mobile-only: Accept/Decline buttons shown at the top of the card
 */
export const MobileTopButtons = ({
  onAccept,
  onDecline,
  approving,
  declining,
}: Omit<PendingRequestActionsProps, 'onViewDetails'>) => {
  const isDisabled = approving || declining;

  return (
    <MobileButtonsTop>
      <Button
        customStyle={[mobileTopButtonStyles, { marginRight: 8 }]}
        customLabelStyle={buttonFontStyle}
        text="Decline"
        onPress={onDecline}
        variant="tertiary"
        loading={declining}
        disabled={isDisabled}
      />
      <Button
        customStyle={mobileTopButtonStyles}
        customLabelStyle={buttonFontStyle}
        text="Accept"
        onPress={onAccept}
        variant="primary"
        loading={approving}
        disabled={isDisabled}
      />
    </MobileButtonsTop>
  );
};

const DesktopButtonsRow = styled.View({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'flex-end',
});

const MobileButtonsTop = styled.View({
  flexDirection: 'row',
  justifyContent: 'center',
  paddingHorizontal: 20,
  marginBottom: 12,
});

const MobileViewDetailsContainer = styled.View({
  alignItems: 'center',
  marginBottom: 10,
});

const desktopButtonStyles = css({
  width: 145,
  height: 42,
  borderWidth: 2,
  marginBottom: 17,
});

const mobileTopButtonStyles = css({
  flex: 1,
  height: 42,
  borderWidth: 2,
  maxWidth: 160,
});

const mobileViewDetailsButtonStyles = css({
  width: 325,
  height: 42,
  borderWidth: 2,
  marginBottom: 10,
});

const buttonFontStyle = css({
  fontSize: 13,
  letterSpacing: -0.13,
  marginHorizontal: 0,
  marginLeft: 0,
  marginRight: 0,
});

export default PendingRequestActions;
